import CardLayout from '@/components/common/CardLayout';
import Modal from '@/components/common/modals/Modal';
import { LinkText, Text } from '@/components/common/Typography';
import { DepositoryAccountSelector } from '@/components/financialEntities/DepositoryAccountSelectionView';
import { useCheckDepositoryAccount } from '@/components/financialEntities/helpers';
import ModalManager, { useModal } from '@/components/ModalManager';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { logError } from '@/util/logger';
import { useMutation, useQuery } from '@apollo/client';
import { Spinner, VStack } from '@chakra-ui/react';
import { Goal } from '@common/goals';
import {
    AllFinancialEntitiesDocument,
    DeleteGoalDocument,
    GoalDeleteCheckDocument,
    LeaveSplitGoalDocument,
} from '@common/graphql/generated';
import { TOASTS } from '@common/toasts';
import { useState } from 'react';
import { useToasts } from '../common/Toasts';

export interface GoalDeleteModalProps {
    goal: Goal;
    onComplete: () => void;
}

export const showGoalDeleteModal = (props: GoalDeleteModalProps) =>
    ModalManager.show(GoalDeleteModal, props);

const GoalDeleteModal = ModalManager.create<GoalDeleteModalProps>(
    ({ goal, onComplete }) => {
        useTrackModalShown('Delete Goal Modal');

        const { showSuccessToast, showErrorToast } = useToasts();
        const modal = useModal();
        const { data: deleteCheckData } = useQuery(GoalDeleteCheckDocument, {
            variables: { id: goal.id },
            fetchPolicy: 'no-cache',
        });
        const deleteCheck = deleteCheckData?.goal?.deleteCheck;
        const [depositoryAccountId, setDepositoryAccountId] = useState<
            string | undefined
        >(undefined);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [isShowingConfirmation, setIsShowingConfirmation] =
            useState(false);
        const [deleteGoal] = useMutation(DeleteGoalDocument, {
            variables: {
                id: goal.id,
                depositoryAccountId,
            },
            refetchQueries: [AllFinancialEntitiesDocument],
        });
        const [leaveSplitGoal] = useMutation(LeaveSplitGoalDocument, {
            variables: {
                id: goal.id,
                depositoryAccountId,
            },
            refetchQueries: [AllFinancialEntitiesDocument],
        });
        const { checkDepositoryAccount, errorMessageByAccountId } =
            useCheckDepositoryAccount({
                setSelectedAccountId: setDepositoryAccountId,
            });
        const { data } = useQuery(AllFinancialEntitiesDocument);
        const selectedAccount = data?.financialEntities?.find(
            (fe) => fe.id === depositoryAccountId
        );

        let content: JSX.Element;
        if (deleteCheck) {
            switch (deleteCheck) {
                case 'ALREADY_DELETED':
                    content = <></>;
                    modal.remove();
                    break;
                case 'CAN_BE_DELETED_ZERO_BALANCE':
                    content = (
                        <CardLayout
                            title={`Are you sure you want to delete this goal?`}
                            primaryButtonText="Delete"
                            onPrimaryButton={async () => {
                                setIsSubmitting(true);
                                try {
                                    await deleteGoal();
                                    showSuccessToast(
                                        TOASTS.deleteGoalZeroBalanceSuccess
                                    );
                                    modal.remove();
                                    onComplete();
                                } catch (err) {
                                    logError(
                                        'Failed to delete goal with zero balance',
                                        { err }
                                    );
                                    showErrorToast(
                                        TOASTS.unexpectedErrorTryAgainLater
                                    );
                                } finally {
                                    setIsSubmitting(false);
                                }
                            }}
                            isPrimaryButtonLoading={isSubmitting}
                            onSecondaryButton={modal.remove}
                            onClickPrimaryEvent="Goal Delete Modal - Confirm Delete Goal Button Clicked"
                            onClickSecondaryEvent="Goal Delete Modal - Cancel Delete Goal Button Clicked"
                        >
                            <Text fontSize="md" color="textMuted">
                                {`It will no longer be visible, and will not contribute to your net worth.`}
                            </Text>
                        </CardLayout>
                    );
                    break;
                case 'CAN_LEAVE_SPLIT_GOAL_ZERO_BALANCE':
                    content = (
                        <CardLayout
                            title={`Are you sure you want to leave this split goal?`}
                            primaryButtonText="Leave Split Goal"
                            onPrimaryButton={async () => {
                                setIsSubmitting(true);
                                try {
                                    await leaveSplitGoal();
                                    showSuccessToast(
                                        TOASTS.leaveSplitGoalZeroBalanceSuccess
                                    );
                                    modal.remove();
                                    onComplete();
                                } catch (err) {
                                    logError(
                                        'Failed to leave split goal with zero balance',
                                        { err }
                                    );
                                    showErrorToast(
                                        TOASTS.unexpectedErrorTryAgainLater
                                    );
                                } finally {
                                    setIsSubmitting(false);
                                }
                            }}
                            isPrimaryButtonLoading={isSubmitting}
                            onSecondaryButton={modal.remove}
                            onClickPrimaryEvent="Goal Delete Modal - Confirm Leave Split Goal Button Clicked"
                            onClickSecondaryEvent="Goal Delete Modal - Cancel Leave Split Goal Button Clicked"
                        >
                            <Text fontSize="md" color="textMuted">
                                {`It will be changed to a private goal for your partner and will not be visible to you unless shared by your partner.`}
                            </Text>
                        </CardLayout>
                    );
                    break;
                case 'CAN_LEAVE_SPLIT_GOAL_HAS_BALANCE':
                    if (isShowingConfirmation) {
                        content = (
                            <CardLayout
                                title={`Are you sure you want to leave this split goal?`}
                                primaryButtonText="Leave Split Goal"
                                onPrimaryButton={
                                    depositoryAccountId
                                        ? async () => {
                                              setIsSubmitting(true);
                                              try {
                                                  await leaveSplitGoal();
                                                  showSuccessToast(
                                                      TOASTS.leaveSplitGoalHasBalanceSuccess(
                                                          {
                                                              feName: selectedAccount?.name,
                                                          }
                                                      )
                                                  );
                                                  modal.remove();
                                                  onComplete();
                                              } catch (err) {
                                                  logError(
                                                      'Failed to leave split goal with balance',
                                                      { err }
                                                  );
                                                  showErrorToast(
                                                      TOASTS.unexpectedErrorTryAgainLater
                                                  );
                                              } finally {
                                                  setIsSubmitting(false);
                                              }
                                          }
                                        : undefined
                                }
                                isPrimaryButtonLoading={isSubmitting}
                                onSecondaryButton={modal.remove}
                                onClickPrimaryEvent="Goal Delete Modal - Confirm Leave Split Goal Button Clicked"
                                onClickSecondaryEvent="Goal Delete Modal - Cancel Leave Split Goal Button Clicked"
                            >
                                <Text fontSize="md" color="textMuted">
                                    {`It will be changed to a private goal for your partner and will not be visible to you unless shared by your partner.`}
                                </Text>
                            </CardLayout>
                        );
                    } else {
                        content = (
                            <CardLayout
                                title={`Select destination account to transfer funds`}
                                primaryButtonText="Select destination"
                                onSecondaryButton={modal.remove}
                                onPrimaryButton={
                                    depositoryAccountId
                                        ? async () => {
                                              setIsSubmitting(true);
                                              await checkDepositoryAccount(
                                                  depositoryAccountId
                                              );
                                              setIsSubmitting(false);
                                              setIsShowingConfirmation(true);
                                          }
                                        : undefined
                                }
                                isPrimaryButtonLoading={isSubmitting}
                                onClickPrimaryEvent="Goal Delete Modal - Select Destination Account Button Clicked"
                                onClickSecondaryEvent="Goal Delete Modal - Cancel Select Destination Account Button Clicked"
                            >
                                <VStack spacing={4} align="stretch">
                                    <Text fontSize="md" color="textMuted">
                                        {`To leave this split goal, please select a destination account to transfer your remaining funds to:`}
                                    </Text>
                                    <DepositoryAccountSelector
                                        selectedAccountId={depositoryAccountId}
                                        onChangeSelectedAccountId={
                                            setDepositoryAccountId
                                        }
                                        errorMessageByAccountId={
                                            errorMessageByAccountId
                                        }
                                    />
                                </VStack>
                            </CardLayout>
                        );
                    }
                    break;
                case 'CAN_BE_DELETED_HAS_BALANCE':
                    if (isShowingConfirmation) {
                        content = (
                            <CardLayout
                                title={`Are you sure you want to delete this goal?`}
                                primaryButtonText="Delete"
                                onPrimaryButton={
                                    depositoryAccountId
                                        ? async () => {
                                              setIsSubmitting(true);
                                              try {
                                                  await deleteGoal();
                                                  showSuccessToast(
                                                      TOASTS.deleteGoalHasBalanceSuccess(
                                                          {
                                                              feName: selectedAccount?.name,
                                                          }
                                                      )
                                                  );
                                                  modal.remove();
                                                  onComplete();
                                              } catch (err) {
                                                  logError(
                                                      'Failed to delete goal with balance',
                                                      { err }
                                                  );
                                                  showErrorToast(
                                                      TOASTS.unexpectedErrorTryAgainLater
                                                  );
                                              } finally {
                                                  setIsSubmitting(false);
                                              }
                                          }
                                        : undefined
                                }
                                isPrimaryButtonLoading={isSubmitting}
                                onSecondaryButton={modal.remove}
                                onClickPrimaryEvent="Goal Delete Modal - Confirm Delete Goal Button Clicked"
                                onClickSecondaryEvent="Goal Delete Modal - Cancel Delete Goal Button Clicked"
                            >
                                <Text fontSize="md" color="textMuted">
                                    {`It will no longer be visible, and will not contribute to your net worth.`}
                                </Text>
                            </CardLayout>
                        );
                    } else {
                        content = (
                            <CardLayout
                                title={`Select destination account to transfer funds`}
                                primaryButtonText="Select destination"
                                onSecondaryButton={modal.remove}
                                onPrimaryButton={
                                    depositoryAccountId
                                        ? async () => {
                                              setIsSubmitting(true);
                                              await checkDepositoryAccount(
                                                  depositoryAccountId
                                              );
                                              setIsSubmitting(false);
                                              setIsShowingConfirmation(true);
                                          }
                                        : undefined
                                }
                                isPrimaryButtonLoading={isSubmitting}
                                onClickPrimaryEvent="Goal Delete Modal - Select Destination Account Button Clicked"
                                onClickSecondaryEvent="Goal Delete Modal - Cancel Select Destination Account Button Clicked"
                            >
                                <VStack spacing={4} align="stretch">
                                    <Text fontSize="md" color="textMuted">
                                        {`To delete a goal, please select a destination account to transfer the funds to:`}
                                    </Text>
                                    <DepositoryAccountSelector
                                        selectedAccountId={depositoryAccountId}
                                        onChangeSelectedAccountId={
                                            setDepositoryAccountId
                                        }
                                        errorMessageByAccountId={
                                            errorMessageByAccountId
                                        }
                                    />
                                </VStack>
                            </CardLayout>
                        );
                    }
                    break;
                case 'PENDING_TRANSACTIONS':
                    content = (
                        <CardLayout
                            title={`Unable to delete goal`}
                            primaryButtonText="Delete"
                            onSecondaryButton={modal.remove}
                            onClickPrimaryEvent="Goal Delete Modal - Confirm Delete Goal Button Clicked"
                            onClickSecondaryEvent="Goal Delete Modal - Cancel Delete Goal Button Clicked"
                        >
                            <Text fontSize="md" color="textMuted">
                                {`This goal has a pending transfer. Please wait for the transfer to complete and then try again.`}
                            </Text>
                        </CardLayout>
                    );
                    break;
                case 'PLENTY_IRA_EXISTS':
                    content = (
                        <CardLayout
                            title={`Unable to delete goal`}
                            primaryButtonText="Delete"
                            onSecondaryButton={modal.remove}
                            onClickPrimaryEvent="Goal Delete Modal - Confirm Delete Goal Button Clicked"
                            onClickSecondaryEvent="Goal Delete Modal - Cancel Delete Goal Button Clicked"
                        >
                            <Text fontSize="md" color="textMuted">
                                This goal includes a Plenty IRA. To delete this
                                goal, please reach out to us at{' '}
                                <LinkText href="mailto:members@withplenty.com">
                                    members@withplenty.com
                                </LinkText>
                                .
                            </Text>
                        </CardLayout>
                    );
                    break;
                default: {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const exhaustiveCheck: never = deleteCheck;
                    content = <></>;
                }
            }
        } else {
            content = <Spinner />;
        }

        return (
            <Modal
                size={
                    deleteCheck === 'CAN_BE_DELETED_HAS_BALANCE' ||
                    deleteCheck === 'CAN_LEAVE_SPLIT_GOAL_HAS_BALANCE'
                        ? 'xl'
                        : 'xs'
                }
                isOpen={modal.visible}
                onClose={modal.remove}
            >
                {content}
            </Modal>
        );
    }
);

export default GoalDeleteModal;
